import { styled } from "@mui/material/styles";
import { Paper, Switch } from "@mui/material";

// Styled components
export const StyledTogglePaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: "12px",
  backgroundColor: "#f9f9f9",
  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
}));

export const StyledSwitch = styled(Switch)(() => ({
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: "#4CAF50",
    "&:hover": {
      backgroundColor: "rgba(76, 175, 80, 0.08)",
    },
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#4CAF50",
  },
}));

// Container styles
export const containerStyles = {
  pt: 0.5,
};

export const mainBoxStyles = {
  width: "100%",
  pb: 4,
  display: "flex",
  flexDirection: "column",
};

export const formContainerStyles = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  pt: 2,
};

// Toggle section styles
export const toggleRowStyles = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  mb: 2,
};

export const toggleTextStyles = {
  mr: 2,
  fontSize: "0.95rem",
};

// Image upload area styles
export const imageContainerStyles = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  py: 2,
  borderRadius: "8px",
  textAlign: "center",
  width: "100%",
};

export const uploadedImageContainerStyles = {
  width: "80%",
  height: "250px",
  position: "relative",
  borderRadius: "12px",
  overflow: "hidden",
  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  mb: 2,
};

export const uploadedImageStyles = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  transition: "transform 0.3s ease",
};

export const uploadInstructionsStyles = {
  maxWidth: "80%",
};

// Upload button styles
export const uploadButtonContainerStyles = {
  display: "flex",
  justifyContent: "center",
  marginTop: "20px",
  gap: "10px",
};

export const fileInputButtonStyles = {
  display: "inline-block",
  padding: "10px 15px",
  backgroundColor: "#4CAF50",
  color: "#fff",
  borderRadius: "5px",
  textAlign: "center",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: "bold",
  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
};

export const cameraButtonStyles = {
  display: "inline-block",
  padding: "10px 15px",
  backgroundColor: "#2196F3",
  color: "#fff",
  borderRadius: "5px",
  textAlign: "center",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: "bold",
  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
};

export const processButtonContainerStyles = {
  width: "100%",
  mt: 2,
  display: "flex",
  justifyContent: "center",
};

// Error alert styles
export const errorContainerStyles = {
  width: "100%",
  mt: 2,
  px: { xs: 2, md: 3 },
  boxSizing: "border-box",
};

export const alertStyles = {
  width: "100%",
  boxSizing: "border-box",
  "& .MuiAlert-message": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
  },
};

// Loader styles
export const loaderContainerStyles = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

// Results section styles
export const resultsContainerStyles = {
  width: "100%",
  pt: 2,
};

export const resultsBoxStyles = {
  width: "100%",
  p: { xs: 2, md: 3 },
  backgroundColor: "#f9f9f9",
  borderRadius: "12px",
  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
  overflow: "hidden",
  boxSizing: "border-box",
};

export const resultsTitleStyles = {
  fontWeight: "bold",
  color: "#2e7d32",
  textAlign: "center",
  mb: 3,
};

// Card styles for result sections
export const resultCardStyles = {
  backgroundColor: "white",
  p: { xs: 1.5, md: 2 },
  borderRadius: "8px",
  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
  height: "100%",
  overflow: "hidden",
  wordBreak: "break-word",
};

export const cardTitleStyles = {
  fontWeight: "600",
  mb: 2,
  color: "#333",
  textAlign: "center",
  fontSize: { xs: "1rem", md: "1.25rem" },
};

// Plant information styles
export const infoRowStyles = {
  display: "flex",
  alignItems: "center",
  mb: 2,
  width: "100%",
};

export const infoIconContainerStyles = (color = "#e8f5e9") => ({
  backgroundColor: color,
  borderRadius: "50%",
  width: 36,
  height: 36,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  mr: 1.5,
  flexShrink: 0,
});

export const infoTextContainerStyles = {
  minWidth: 0,
  flexGrow: 1,
  overflow: "hidden",
};

export const infoLabelStyles = {
  display: "block",
  textOverflow: "ellipsis",
};

export const infoValueStyles = {
  wordBreak: "break-word",
  overflowWrap: "break-word",
  hyphens: "auto",
};

// Disease severity color mapping
export const getSeverityColor = (severity) => {
  if (!severity || severity === "N/A") return "#f5f5f5";

  if (severity.toLowerCase().includes("high")) return "#ffebee";
  if (severity.toLowerCase().includes("medium")) return "#fff8e1";
  return "#e8f5e9";
};

export const getSeverityTextColor = (severity) => {
  if (!severity || severity === "N/A") return "#757575";

  if (severity.toLowerCase().includes("high")) return "#d32f2f";
  if (severity.toLowerCase().includes("medium")) return "#f57c00";
  return "#2e7d32";
};

// Description styles
export const descriptionStyles = {
  lineHeight: 1.6,
  overflowWrap: "break-word",
};

// List item styles
export const listContainerStyles = {
  pl: 2,
  m: 0,
};

export const listItemStyles = {
  mb: 1.5,
  color: "text.secondary",
  "&::marker": { color: "#4CAF50" },
};

// Timeline step styles
export const stepContainerStyles = {
  display: "flex",
  flexDirection: "column",
};

export const stepRowStyles = {
  display: "flex",
  mb: 1.5,
  alignItems: "flex-start",
};

export const stepNumberStyles = (color = "#e8f5e9", textColor = "#2e7d32") => ({
  backgroundColor: color,
  color: textColor,
  borderRadius: "50%",
  width: 24,
  height: 24,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  mr: 1.5,
  fontWeight: "bold",
  fontSize: "14px",
  flexShrink: 0,
});

export const bulletPointStyles = {
  backgroundColor: "#fff3e0",
  color: "#e65100",
  borderRadius: "50%",
  width: 24,
  height: 24,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  mr: 1.5,
  fontWeight: "bold",
  fontSize: "14px",
  flexShrink: 0,
};

export const stepTextStyles = {
  pt: 0.2,
  overflowWrap: "break-word",
};

// Confidence level styles
export const confidenceLevelContainerStyles = {
  mt: 2,
  display: "flex",
  alignItems: "center",
};

export const getConfidenceLevelColor = (level) => {
  if (!level) return "#757575";

  if (level.toLowerCase().includes("high")) return "#2e7d32";
  if (level.toLowerCase().includes("medium")) return "#f57c00";
  return "#d32f2f";
};

// Action button styles
export const actionButtonContainerStyles = {
  display: "flex",
  justifyContent: "center",
  mt: 4,
};
